import { AlertFactory } from 'alerts/Alert'
import { ConfirmViewModel } from 'components/ConfirmDialog/ConfirmViewModel'
import { ViewModelList } from 'infra/ViewModelList'
import { Id } from 'types/definitions'
import Job from 'jobs/Job'
import { JobNote } from 'jobs/notes/JobNote'
import { JobNoteStore } from 'jobs/notes/JobNoteStore'
import InfiniteScrollProvider from 'utils/InfiniteScrollProvider'
import JobNoteItemViewModel from './JobNoteItemViewModel'
import AddJobNoteDialogViewModel, {
  JobNoteRecipient,
} from './AddJobNoteDialog/AddJobNoteDialogViewModel'
import Member from 'iam/models/Member'
import { action, computed, makeObservable, observable } from 'mobx'
import { Subscription } from 'rxjs'
import { delay } from 'rxjs/operators'
import ModalViewModel from 'infra/ModalViewModel'

// Highlight in MS when job note toggle is pressed
const HIGHLIGHT_LINGER = 4000

/**
 * View model for the job notes card.
 */
export default class JobNotesViewModel {
  confirmDeleteNote = new ConfirmViewModel()

  movedNotice = new ModalViewModel()

  isHighlighted = false

  /**
   * Dialog for adding a note.
   */
  addNoteVM = new AddJobNoteDialogViewModel(
    async (body, recipientUserPublicIds) => {
      await this.store.createForJob(this.job.id, body, recipientUserPublicIds)
      await this.scroller.fetch()
    },
    this.newAlert,
    this.getNotifyRecipients,
  )

  scroller = new InfiniteScrollProvider<JobNote>({
    limit: 20,
    fetchItems: (paging) => this.store.listForJob(this.job.id, paging),
  })

  private subscription = new Subscription()

  /**
   * Note view models newest first.
   */
  private notesList = new ViewModelList({
    models: () => this.store.notesForJob(this.job.id),
    create: (note: JobNote) =>
      new JobNoteItemViewModel(note, this.getMentionLabel),
  })

  constructor(
    private readonly job: Job,
    private readonly store: JobNoteStore,
    private readonly newAlert: AlertFactory,
    private readonly isProviderCollaborator: () => boolean,
    private readonly getWorkspaceMember: () => Member,
    private readonly getNotifyRecipients: () => JobNoteRecipient[],
    private readonly fetchRelationManager: () => Promise<void>,
    private readonly getMentionLabel: (userPublicId: Id) => string | undefined,
  ) {
    makeObservable(this, {
      notes: computed,
      canView: computed,
      canWrite: computed,
      isHighlighted: observable,
      revealFromChat: action.bound,
    })
  }

  revealFromChat() {
    this.isHighlighted = true
    this.movedNotice.show()
  }

  get notes() {
    return this.notesList.items
  }

  private get canManage() {
    return this.getWorkspaceMember().hasPermission('WORKSPACE_JOB_NOTE_MANAGE')
  }

  get canView() {
    return this.isProviderCollaborator() || this.canManage
  }

  get canWrite() {
    return this.isProviderCollaborator() || this.canManage
  }

  canDelete(note: JobNoteItemViewModel) {
    return (
      this.canManage ||
      (this.isProviderCollaborator() && note.author.isCurrentUser)
    )
  }

  activate() {
    this.notesList.activate()
    void this.fetchRelationManager()
    this.subscription.add(
      this.movedNotice.close$.pipe(delay(HIGHLIGHT_LINGER)).subscribe(
        action(() => {
          this.isHighlighted = false
        }),
      ),
    )
    this.subscription.add(
      this.store.events$.subscribe((event) => {
        if (event.jobId !== this.job.id) {
          return
        }

        if (event.type === 'JobNoteArchived') {
          this.store.removeNoteById(event.noteId)
          return
        }

        this.scroller.fetch()
      }),
    )
    return this.scroller.fetch()
  }

  deactivate() {
    this.notesList.deactivate()
    this.subscription.unsubscribe()
    this.subscription = new Subscription()
  }

  deleteNote(note: JobNoteItemViewModel) {
    this.confirmDeleteNote.show(() => this.archiveNote(note.id))
  }

  private async archiveNote(noteId: Id) {
    try {
      await this.store.archive(noteId)
    } catch (err) {
      this.newAlert().fromError(err).autoDismiss().show()
    }
  }
}
